import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, Download } from 'lucide-react';

const pad2 = (n) => String(n).padStart(2, '0');
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const formatMMSS = (sec) => {
  const s = Number.isFinite(sec) ? Math.max(0, Math.floor(sec)) : 0;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${pad2(r)}`;
};

/**
 * CallRecordingPlayer (single-recording focused)
 * - Screenshot-like seek bar (red progress + dark remaining)
 * - Play/Pause
 * - Shows current time / duration
 * - Optional Download (useful in details view)
 *
 * Props:
 * - src: full URL of the audio file
 * - compact: smaller layout (for tables) — hides extra buttons to prevent overflow
 * - showDownload: show download button (default: !compact)
 * - className: wrapper classes
 */
const CallRecordingPlayer = ({
  src,
  compact = false,
  showDownload,
  className = '',
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [hasError, setHasError] = useState(false);

  const safeSrc = typeof src === 'string' && src.trim() ? src.trim() : '';

  const max = useMemo(() => (Number.isFinite(duration) && duration > 0 ? duration : 0), [duration]);
  const value = useMemo(() => clamp(currentTime, 0, max || 0), [currentTime, max]);
  const pct = useMemo(() => (max > 0 ? (value / max) * 100 : 0), [value, max]);
  const trackStyle = useMemo(
    () => ({
      background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${pct}%, #111827 ${pct}%, #111827 100%)`,
    }),
    [pct]
  );

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;

    const onLoaded = () => setDuration(Number(a.duration) || 0);
    const onTime = () => {
      if (!isSeeking) setCurrentTime(Number(a.currentTime) || 0);
    };
    const onPlay = () => {
      setHasError(false);
      setIsPlaying(true);
    };
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);
    const onError = () => {
      setHasError(true);
      setIsPlaying(false);
    };

    a.addEventListener('loadedmetadata', onLoaded);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    a.addEventListener('error', onError);

    return () => {
      a.removeEventListener('loadedmetadata', onLoaded);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('error', onError);
    };
  }, [isSeeking]);

  // Reset state when src changes
  useEffect(() => {
    setHasError(false);
    setIsPlaying(false);
    setDuration(0);
    setCurrentTime(0);
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    // force reload metadata
    a.load();
  }, [safeSrc]);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      if (a.paused || a.ended) {
        await a.play();
      } else {
        a.pause();
      }
    } catch {
      setHasError(true);
    }
  };

  const download = () => {
    if (!safeSrc) return;
    window.open(safeSrc, '_blank', 'noopener,noreferrer');
  };

  if (!safeSrc) return <span className="text-xs text-gray-400">No recording</span>;

  const rootPad = compact ? 'px-2 py-2' : 'px-4 py-3';
  const iconBtn = compact ? 'h-8 w-8' : 'h-10 w-10';
  const playBtn = compact ? 'h-10 w-10' : 'h-12 w-12';
  const playIcon = compact ? 18 : 22;
  const smallIcon = compact ? 18 : 20;
  const trackH = compact ? 'h-2' : 'h-2.5';
  const timeText = compact ? 'text-[10px]' : 'text-xs';
  const showDownloadBtn = typeof showDownload === 'boolean' ? showDownload : !compact;

  return (
    <div className={['rounded-xl border border-gray-200 bg-white overflow-hidden', rootPad, className].join(' ')}>
      {/* Seek bar (screenshot-like) */}
      <div className="w-full">
        <input
          type="range"
          min={0}
          max={max || 0}
          step={0.25}
          value={value}
          onMouseDown={() => setIsSeeking(true)}
          onMouseUp={() => setIsSeeking(false)}
          onTouchStart={() => setIsSeeking(true)}
          onTouchEnd={() => setIsSeeking(false)}
          onChange={(e) => {
            const next = clamp(parseFloat(e.target.value), 0, max || 0);
            setCurrentTime(next);
            const a = audioRef.current;
            if (a) a.currentTime = next;
          }}
          style={trackStyle}
          className={[
            'w-full cursor-pointer appearance-none rounded-full',
            trackH,
            // thumb
            '[&::-webkit-slider-thumb]:appearance-none',
            '[&::-webkit-slider-thumb]:h-4',
            '[&::-webkit-slider-thumb]:w-4',
            '[&::-webkit-slider-thumb]:rounded-full',
            '[&::-webkit-slider-thumb]:bg-white',
            '[&::-webkit-slider-thumb]:shadow',
            '[&::-webkit-slider-thumb]:border',
            '[&::-webkit-slider-thumb]:border-gray-300',
            '[&::-moz-range-thumb]:h-4',
            '[&::-moz-range-thumb]:w-4',
            '[&::-moz-range-thumb]:rounded-full',
            '[&::-moz-range-thumb]:bg-white',
            '[&::-moz-range-thumb]:border',
            '[&::-moz-range-thumb]:border-gray-300',
          ].join(' ')}
          aria-label="Seek recording"
        />
        <div className={['mt-1 flex items-center justify-between text-gray-500', timeText].join(' ')}>
          <span className="tabular-nums">{formatMMSS(value)}</span>
          <span className="tabular-nums">{formatMMSS(max)}</span>
        </div>
      </div>

      {/* Controls row (single-recording controls) */}
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2" />

        <button
          type="button"
          onClick={toggle}
          className={`${playBtn} inline-flex items-center justify-center rounded-full bg-gray-900 hover:bg-black text-white shadow`}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause size={playIcon} /> : <Play size={playIcon} className="ml-0.5" />}
        </button>

        <div className="flex items-center gap-2 justify-end">
          {showDownloadBtn ? (
            <button
              type="button"
              onClick={download}
              className={`${iconBtn} inline-flex items-center justify-center rounded-lg hover:bg-gray-50 text-gray-700`}
              title="Download"
            >
              <Download size={smallIcon} />
            </button>
          ) : null}
        </div>
      </div>

      <audio ref={audioRef} src={safeSrc} preload="none" />

      {hasError ? (
        <div className="mt-2 text-[11px] text-red-600">Audio error</div>
      ) : null}
    </div>
  );
};

export default CallRecordingPlayer;

